import React, { useState } from "react";
import { PulseLoader } from "react-spinners";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import * as c from "./emailCaptureForm.module.scss";
import { getVisitInfo } from "@utils/visitTracking";
import SubmitButton from "@components/ui/submitButton";
import TextInput from "@components/ui/textInput";
import IconCheckmarkAlt from "@components/icons/iconCheckmarkAlt";
import { Button } from "@components/shared/button/button";

const EmailCaptureForm = ({
  className,
  formSource = "email_capture_form",
  variant = "inline",
  inputPlaceholder = "Your email address",
  buttonText = "Subscribe",
  successMessage = "Thank you for subscribing!",
  businessTypes = [],
}) => {
  const variantValue = Array.isArray(variant) ? variant[0] : variant;
  const isInline = variantValue.toLowerCase() === "inline";
  const [error, setError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, errors },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    setError(null);
    try {
      await fetch(`https://f8646094523dac6449902191436c425a.m.pipedream.net`, {
        method: `POST`,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          signup: data,
          form_source: formSource,
          page_title: document.title,
          page_url: window.location.href,
          firstVisit: getVisitInfo("first", false),
          currentVisit: getVisitInfo("current", false),
          business_types: businessTypes,
        }),
      });
      reset();
    } catch (error) {
      setError("An unknown error occurred. Please send us a chat message.");
      console.error(error);
    }
  };

  const emailValidationRules = {
    required: "Please fill in your email address",
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: "Invalid email address",
    },
  };

  const renderLoader = () => (
    <PulseLoader
      color="#fff"
      cssOverride={{
        margin: "0 auto",
      }}
      size="8px"
    />
  );

  const ErrorMessage = ({ message }) => (
    <p className={c.error}>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 5.69635V8.58458M8 11.4728H8.00682M7.0556 2.00789L1.44595 12.2659C1.13481 12.8348 0.979233 13.1193 1.00223 13.3528C1.02228 13.5565 1.12307 13.7415 1.2795 13.8619C1.45884 14 1.76935 14 2.39035 14H13.6096C14.2307 14 14.5412 14 14.7205 13.8619C14.8769 13.7415 14.9777 13.5565 14.9978 13.3528C15.0208 13.1193 14.8652 12.8348 14.554 12.2659L8.9444 2.00789C8.63437 1.44096 8.47935 1.15749 8.27711 1.06228C8.1007 0.979239 7.8993 0.979239 7.72289 1.06228C7.52065 1.15749 7.36563 1.44096 7.0556 2.00789Z"
          stroke="#BC269B"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {message}
    </p>
  );

  const renderInlineForm = () => {
    if (isSubmitted && !error) {
      return (
        <div className={classNames(c.success, className)}>
          <IconCheckmarkAlt className={c.fadeIn} />
          <p className={classNames(c.message, c.fadeIn)}>{successMessage}</p>
        </div>
      );
    }

    return (
      <form className={classNames(c.inlineForm, className)} onSubmit={handleSubmit(onSubmit)}>
        {error && <ErrorMessage message={error} />}
        <TextInput
          className={classNames(c.textField, { [c.hasError]: error })}
          tabIndex={1}
          type="email"
          placeholder={inputPlaceholder}
          errors={errors}
          {...register("email", emailValidationRules)}
          v2={true}
          disabled={isSubmitting}
          required={true}
        >
          <SubmitButton tabIndex={2} className={c.submitButton} v2={true} disabled={isSubmitting}>
            {isSubmitting ? renderLoader() : <>{buttonText}</>}
          </SubmitButton>
        </TextInput>
      </form>
    );
  };

  const renderStackedForm = () => {
    if (isSubmitted && !error) {
      return (
        <div className={c.stackedForm}>
          <div className={c.successMessage}>
            <IconCheckmarkAlt className={c.fadeIn} />
            <p className="p3">{successMessage}</p>
          </div>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)} className={c.stackedForm}>
        {error && <ErrorMessage message={error} />}
        <TextInput
          className={classNames(c.input, { [c.hasError]: error })}
          type="email"
          placeholder={inputPlaceholder}
          errors={errors}
          {...register("email", emailValidationRules)}
          disabled={isSubmitting}
          required
        />
        <Button
          className={classNames(c.button, { [c.hasError]: error })}
          variant="full"
          size="large"
          asHtmlButton
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? renderLoader() : <>{buttonText}</>}
        </Button>
      </form>
    );
  };

  return isInline ? renderInlineForm() : renderStackedForm();
};

export default EmailCaptureForm;
